import {
  Box,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material'
import { LineChart } from '@mui/x-charts/LineChart'
import { useTeamDviTrend } from '@src/api/dvi-api'
import PageLoader from '@src/components/Loader/PageLoader'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

type Period = 'YTD' | 'PAST_12M'

const TeamDviTab = () => {
  const { t } = useTranslation()
  const [period, setPeriod] = useState<Period>('YTD')

  // Helper function to find the first Monday on or before a given date
  const getFirstMonday = (date: Date): Date => {
    const dayOfWeek = date.getDay()
    const daysUntilMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1 // Sunday is 0, Monday is 1
    const monday = new Date(date)
    monday.setDate(date.getDate() - daysUntilMonday)
    return monday
  }

  // Calculate date range based on selected period
  const { fromDate, toDate } = useMemo(() => {
    const today = new Date()

    if (period === 'YTD') {
      // Year to date: First Monday of current year to today
      const yearStart = new Date(today.getFullYear(), 0, 1)
      const firstMonday = getFirstMonday(yearStart)
      return {
        fromDate: firstMonday.toISOString().split('T')[0],
        toDate: today.toISOString().split('T')[0],
      }
    } else {
      // Last 52 weeks: First Monday 52 weeks ago to today
      const fiftyTwoWeeksAgo = new Date(today)
      fiftyTwoWeeksAgo.setDate(today.getDate() - 364) // 52 weeks = 364 days
      const firstMonday = getFirstMonday(fiftyTwoWeeksAgo)
      return {
        fromDate: firstMonday.toISOString().split('T')[0],
        toDate: today.toISOString().split('T')[0],
      }
    }
  }, [period])

  const [data, isFetching] = useTeamDviTrend(fromDate, toDate)

  // Calculate average DVI
  const averageDvi = useMemo(() => {
    if (!data || data.length === 0) return 0
    const sum = data.reduce((acc, item) => acc + item.dvi, 0)
    return sum / data.length
  }, [data])

  const handlePeriodChange = (event: SelectChangeEvent<Period>) => {
    setPeriod(event.target.value as Period)
  }

  if (isFetching) {
    return <PageLoader />
  }

  if (!data || data.length === 0) {
    return (
      <Box>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>{t('dvi.period.label')}</InputLabel>
            <Select value={period} onChange={handlePeriodChange} label={t('dvi.period.label')}>
              <MenuItem value="YTD">{t('dvi.period.ytd')}</MenuItem>
              <MenuItem value="PAST_12M">{t('dvi.period.past12M')}</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Card>
          <CardContent>
            <Typography variant="body1" color="textSecondary">
              {t('dvi.noData')}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>{t('dvi.period.label')}</InputLabel>
          <Select value={period} onChange={handlePeriodChange} label={t('dvi.period.label')}>
            <MenuItem value="YTD">{t('dvi.period.ytd')}</MenuItem>
            <MenuItem value="PAST_12M">{t('dvi.period.past12M')}</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('dvi.teamChart.title')}
          </Typography>
          <LineChart
            xAxis={[
              {
                scaleType: 'band',
                data: data.map((m) => m.periodLabel),
                tickInterval: data.map((m) => m.periodLabel),
                label: t('dvi.chart.xAxis'),
              },
            ]}
            series={[
              {
                data: data.map((m) => m.dvi),
                label: t('dvi.chart.dviLabel'),
                curve: 'linear',
              },
              {
                data: data.map(() => averageDvi),
                label: 'Average DVI',
                curve: 'linear',
                color: 'orange',
              },
            ]}
            height={400}
            margin={{ bottom: 80 }}
          />
        </CardContent>
      </Card>

      <Box sx={{ mt: 3 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('dvi.teamChart.details')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {data.map((periodData) => (
                <Box key={periodData.periodLabel} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Typography variant="body2" sx={{ minWidth: 120 }}>
                    <strong>{periodData.periodLabel}:</strong>
                  </Typography>
                  <Typography variant="body2">DVI: {periodData.dvi.toFixed(2)}</Typography>
                  <Typography variant="body2">PRs: {periodData.totalPRs}</Typography>
                  <Typography variant="body2">Commits: {periodData.totalCommits}</Typography>
                  <Typography variant="body2">Days: {Math.round(periodData.totalHours / 7.5).toFixed(1)}</Typography>
                  <Typography variant="body2">People: {periodData.personCount}</Typography>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  )
}

export default TeamDviTab
